import { Box, TextField } from '@mui/material';
import { memo, useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { DeleteCircle } from '~/assets/icons';
import { BaseSelect } from '~/components/common';
import { useAppSelector } from '~/redux/hooks';
import { cast } from '~/utils/helper';
import { IResponseEstimateItem } from '~/utils/interface/estimate';
import { IPriceItemList } from '~/utils/interface/priceItem';

type PriceItemProps = {
  data: any;
  index: number;
  priceListItems: Array<IResponseEstimateItem>;
  onItemChange: (item: any, index: number) => void;
};

const PriceItem = (props: PriceItemProps) => {
  //#region Destructuring Props
  const { data, index, priceListItems } = props;
  //#endregion Destructuring Props
  const { areaScopes } = useAppSelector((state) => state.areaScope);

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  const { units } = useAppSelector((state) => state.master);
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Implement Hook
  const [price, setPrice] = useState<any>();
  useEffect(() => {
    setPrice(data);
  }, [data]);
  //#endregion Implement Hook
  //#region Handle Function
  const handleOnChange = (event: React.ChangeEvent<HTMLButtonElement>, index: number) => {
    const priceItem = priceListItems.find((item: any) => Number(item.priceItemId) === Number(event.target.value));

    if (priceItem) {
      props.onItemChange(priceItem, index);
      setPrice(priceItem);
    }
  };

  const handleDelete = (index: number) => {
    props.onItemChange(null, index);
  };

  const calculateItemAmount = (item: IPriceItemList) => {
    const subtotal = (Number(item.unitPrice) || 0) * (Number(item.quantity) || 1);
    return subtotal + (subtotal * (item?.taxPercent || 0)) / 100;
  };

  const updatedList = priceListItems.map((item) => ({
    name: item.name,
    id: item.priceItemId,
  }));
  //#endregion Handle Function

  return (
    <Box className={'flex justify-between items-center gap-[20px] my-3 w-full bg-[#ffffff]'} key={index}>
      <Box className='gap-[20px] w-full flex'>
        <Box className='flex-[0_0_10%]'>
          <BaseSelect
            className='w-full'
            value={cast(data?.priceItemId)}
            options={updatedList}
            onChange={(event) => handleOnChange(event, index)}
          ></BaseSelect>
        </Box>

        <Box className='flex-[0_0_7%]'>
          <TextField disabled value={cast(data?.priceItemCode)} />
        </Box>

        <Box className='flex-[0_0_10%]'>
          <NumericFormat
            customInput={TextField}
            value={cast(data?.quantity)}
            onValueChange={(val) => {
              let newPrice = { ...price, quantity: Number(val.value) ?? 0 };
              newPrice = { ...newPrice, amount: calculateItemAmount(newPrice) };
              props.onItemChange(newPrice, index);
              setPrice(newPrice);
            }}
            allowNegative={false}
            thousandSeparator={true}
          />
        </Box>

        <Box className='flex-[0_0_13%]'>
          <TextField
            value={cast(data?.description)}
            onChange={(e) => {
              const newPrice = { ...price, description: e.target.value };
              props.onItemChange(newPrice, index);
              setPrice(newPrice);
            }}
          />
        </Box>

        <Box className='flex-[0_0_10%]'>
          <NumericFormat
            customInput={TextField}
            value={cast(data?.taxPercent)}
            onValueChange={(val) => {
              const tax = val.floatValue ?? 0;
              let newPrice = { ...price, taxPercent: tax };
              newPrice = { ...newPrice, amount: calculateItemAmount(newPrice) };
              props.onItemChange(newPrice, index);
              setPrice(newPrice);
            }}
            allowNegative={false}
            thousandSeparator={true}
          />
        </Box>

        <Box className='flex-[0_0_10%]'>
          <NumericFormat
            customInput={TextField}
            value={cast(data?.unitPrice)}
            onValueChange={(val) => {
              let newPrice = { ...price, unitPrice: val.value ?? 0 };
              newPrice = { ...newPrice, amount: calculateItemAmount(newPrice) };
              props.onItemChange(newPrice, index);
              setPrice(newPrice);
            }}
            thousandSeparator={true}
            prefix={'$'}
            decimalScale={2}
          />
        </Box>
        <Box className='flex-[0_0_15%]'>
          <BaseSelect
            name={`estimateItems.${index}.unit`}
            value={data?.unit ?? ''}
            options={units}
            onChange={(event) => {
              const newUnit = event.target.value;
              const updatedData = { ...data, unit: newUnit };
              props.onItemChange(updatedData, index);
              setPrice(updatedData);
            }}
          />
        </Box>
        <Box className='flex-[0_0_15%]'>
          <BaseSelect
            name={`estimateItems.${index}.location`}
            value={data?.location ?? ''}
            options={[{ id: 'job', name: 'Job' }, ...areaScopes]}
            onChange={(event) => {
              const newLocation = event.target.value;
              const updatedData = { ...data, location: newLocation };
              props.onItemChange(updatedData, index);
              setPrice(updatedData);
            }}
          />
        </Box>
      </Box>
      <Box>
        <DeleteCircle className='w-[20px] h-[20px]' onClick={() => handleDelete(index)} />
      </Box>
    </Box>
  );
};

export default memo(PriceItem);
